import {
  Box,
  Button,
  HStack,
  VStack,
  Text,
  Select,
  Input,
  Link,
  useToast,
  Collapse
} from "@chakra-ui/react";
import * as XLSX from "xlsx";
import { excelCellValue } from "../../utils/table_export";
import DealLink from "../deal_link";
import { saveAs } from "file-saver";
import React, { useState, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "../../store/store_context";
import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import DateFilters from "./date_filters";
import ColumnValueFilter, { HeaderTitle, ResetColumnFiltersButton, useColumnFilters } from "../column_value_filter";
import { rowsPassing } from "../../utils/column_value_filter";
const DataTable = observer(({ data, renderButtons }) => {
  const { pageStore } = useStores();
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null,
  });
  const toast = useToast();

  const columns = data.length > 0 ? Object.keys(data[0]) : [];

  // Функция для рендеринга ячейки с учетом типа содержимого
  const renderCellContent = (columnKey, value) => {
    // Для столбца IdDeal создаем ссылку
    if (columnKey === "IdDeal" && value) {
      return <DealLink id={value} />;
    }

    // По умолчанию возвращаем отформатированное значение
    return formatCellValue(value);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "desc" };
    });
  };

  const getMonday = (d) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(date.getFullYear(), date.getMonth(), diff);
  };

  const filterByForecastDate = (data, filterType, field, customStart, customEnd) => {
    if (!filterType || filterType === "none") return data;
    if (filterType === "fill") {
      return data.filter((item) => item[field] != null && item[field] !== "");
    }
    if (filterType === "empty") {
      return data.filter((item) => !item[field] || item[field] === "");
    }

    const currentDate = new Date();
    let startStr = null;
    let endStr = null;

    switch (filterType) {
      case "week": {
        const monday = getMonday(currentDate);
        startStr = pageStore.toISODateString(monday);
        endStr = pageStore.toISODateString(new Date(monday.getTime() + 6 * 24 * 60 * 60 * 1000));
        break;
      }
      case "nextWeek": {
        const monday = getMonday(currentDate);
        const nextMonday = new Date(monday.getTime() + 7 * 24 * 60 * 60 * 1000);
        startStr = pageStore.toISODateString(nextMonday);
        endStr = pageStore.toISODateString(new Date(nextMonday.getTime() + 6 * 24 * 60 * 60 * 1000));
        break;
      }
      case "month":
        startStr = pageStore.toISODateString(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
        endStr = pageStore.toISODateString(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0));
        break;
      case "nextMonth":
        startStr = pageStore.toISODateString(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
        endStr = pageStore.toISODateString(new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0));
        break;
      case "pastMonth":
        startStr = pageStore.toISODateString(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
        endStr = pageStore.toISODateString(new Date(currentDate.getFullYear(), currentDate.getMonth(), 0));
        break;
      case "quarter": {
        const quarter = Math.floor(currentDate.getMonth() / 3);
        startStr = pageStore.toISODateString(new Date(currentDate.getFullYear(), quarter * 3, 1));
        endStr = pageStore.toISODateString(new Date(currentDate.getFullYear(), quarter * 3 + 3, 0));
        break;
      }
      case "nextQuarter": {
        const quarter = Math.floor(currentDate.getMonth() / 3) + 1;
        const y = currentDate.getFullYear() + Math.floor(quarter / 4);
        const q = quarter % 4;
        startStr = pageStore.toISODateString(new Date(y, q * 3, 1));
        endStr = pageStore.toISODateString(new Date(y, q * 3 + 3, 0));
        break;
      }
      case "halfYear": {
        const half = Math.floor(currentDate.getMonth() / 6);
        startStr = pageStore.toISODateString(new Date(currentDate.getFullYear(), half * 6, 1));
        endStr = pageStore.toISODateString(new Date(currentDate.getFullYear(), half * 6 + 6, 0));
        break;
      }
      case "nextHalfYear": {
        const y = currentDate.getFullYear();
        if (currentDate.getMonth() < 6) {
          startStr = pageStore.toISODateString(new Date(y, 6, 1));
          endStr = pageStore.toISODateString(new Date(y, 11, 31));
        } else {
          startStr = pageStore.toISODateString(new Date(y + 1, 0, 1));
          endStr = pageStore.toISODateString(new Date(y + 1, 5, 30));
        }
        break;
      }
      case "year":
        startStr = pageStore.toISODateString(new Date(currentDate.getFullYear(), 0, 1));
        endStr = pageStore.toISODateString(new Date(currentDate.getFullYear(), 11, 31));
        break;
      case "nextYear":
        startStr = pageStore.toISODateString(new Date(currentDate.getFullYear() + 1, 0, 1));
        endStr = pageStore.toISODateString(new Date(currentDate.getFullYear() + 1, 11, 31));
        break;
      case "pastYear":
        startStr = pageStore.toISODateString(new Date(currentDate.getFullYear() - 1, 0, 1));
        endStr = pageStore.toISODateString(new Date(currentDate.getFullYear() - 1, 11, 31));
        break;
      case "custom":
        if (customStart && customEnd) {
          startStr = customStart;
          endStr = customEnd;
        }
        break;
      default:
        break;
    }

    if (startStr && endStr) {
      return data.filter((item) => item[field] && item[field] >= startStr && item[field] <= endStr);
    }
    return data;
  };

  const matchesShipmentPercent = (value, filters) => {
    const selected = Object.entries(filters || {}).filter(([, on]) => on).map(([k]) => k);
    if (!selected.length) return true;
    const pct = Number(value);
    const n = Number.isFinite(pct) ? pct : 0;
    return selected.some((key) => {
      if (key === "0") return n === 0;
      if (key === "1-98") return n >= 1 && n <= 98;
      if (key === "50") return n === 50;
      if (key === "99") return n === 99;
      if (key === "100") return n === 100;
      return false;
    });
  };

  const sortedResult = useMemo(() => {
    // Название фильтра дат, который обнулил выборку; null — обнулил не он.
    let emptyReason = null;
    let sortableData = [...data];

    if (pageStore.order_num_filter) {
      sortableData = sortableData.filter(
        (item) =>
        (item.Num &&
          item.Num.toLowerCase().includes(pageStore.order_num_filter.toLowerCase()))
      );
    }

    if (pageStore.order_deal_filter) {
      sortableData = sortableData.filter(
        (item) =>
          (item.IdDeal && item.IdDeal.includes(pageStore.order_deal_filter))
      )
    }

    if (pageStore.workers) {
      sortableData = sortableData.filter(
        (item) =>
          (item.active)
      )
    }

    if (pageStore.isSelf) {
      sortableData = sortableData.filter(
        (item) => ["ТД АЛЬЯНС ФОРЕСТ ООО", "АЛЬЯНС ФОРЕСТ СПБ ООО", "АРТИГЛИО ООО", "ЛОДЖИСТИК ИНДАСТРИ ООО"].includes(item.Partner)
      )
    } else {
      sortableData = sortableData.filter(
        (item) => !["ТД АЛЬЯНС ФОРЕСТ ООО", "АЛЬЯНС ФОРЕСТ СПБ ООО", "АРТИГЛИО ООО", "ЛОДЖИСТИК ИНДАСТРИ ООО"].includes(item.Partner)
      )
    }

    if (pageStore.selected_view_orders === "departament-withoutROP") {
      sortableData = sortableData.filter(
        (item) => item.role_name !== "Руководитель отдела продаж" && item.role_name !== "Руководитель удалённого отдела продаж"
      )
    }

    if (pageStore.order_type_date_start_filter === "none") {
      // Сбрасываем фильтры дат открытия при выборе "Без фильтра"
      pageStore.setOrderStartOpenDateFilter(null);
      pageStore.setOrderEndOpenDateFilter(null);
    }
    if (pageStore.order_type_date_end_filter === "none") {
      // Сбрасываем фильтры дат закрытия при выборе "Без фильтра"
      pageStore.setOrderStartCloseDateFilter(null);
      pageStore.setOrderEndCloseDateFilter(null);
    }

    // Фильтр по заполненности даты открытия
    if (pageStore.order_type_date_start_filter === "fill") {
      sortableData = sortableData.filter(item => item.AccOpenDate != null && item.AccOpenDate !== '');
    } else if (pageStore.order_type_date_start_filter === "empty") {
      sortableData = sortableData.filter(item => !item.AccOpenDate || item.AccOpenDate === '');
    } else if (pageStore.order_type_date_start_filter !== "none") {
      // Фильтр по дате открытия счета (только если не выбраны fill/empty/none)
      let startStr = null;
      let endStr = null;
      const currentDate = new Date();

      switch (pageStore.order_type_date_start_filter) {
        case "week":
          const monday = getMonday(currentDate);
          startStr = pageStore.toISODateString(monday);
          endStr = pageStore.toISODateString(
            new Date(monday.getTime() + 6 * 24 * 60 * 60 * 1000)
          );
          break;
        case "month":
          startStr = pageStore.toISODateString(
            new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
          );
          endStr = pageStore.toISODateString(
            new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
          );
          break;
        case "pastMonth":
          startStr = pageStore.toISODateString(
            new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
          );
          endStr = pageStore.toISODateString(
            new Date(currentDate.getFullYear(), currentDate.getMonth(), 0)
          );
          break;
        case "quarter":
          const quarter = Math.floor(currentDate.getMonth() / 3);
          startStr = pageStore.toISODateString(
            new Date(currentDate.getFullYear(), quarter * 3, 1)
          );
          endStr = pageStore.toISODateString(
            new Date(currentDate.getFullYear(), quarter * 3 + 3, 0)
          );
          break;
        case "halfYear":
          const half = Math.floor(currentDate.getMonth() / 6);
          startStr = pageStore.toISODateString(
            new Date(currentDate.getFullYear(), half * 6, 1)
          );
          endStr = pageStore.toISODateString(
            new Date(currentDate.getFullYear(), half * 6 + 6, 0)
          );
          break;
        case "year":
          startStr = pageStore.toISODateString(new Date(currentDate.getFullYear(), 0, 1));
          endStr = pageStore.toISODateString(
            new Date(currentDate.getFullYear(), 11, 31)
          );
          break;
        case "pastYear":
          startStr = pageStore.toISODateString(new Date(currentDate.getFullYear() - 1, 0, 1));
          endStr = pageStore.toISODateString(
            new Date(currentDate.getFullYear() - 1, 11, 31)
          )
          break;
        case "custom":
          if (pageStore.order_start_open_date_filter && pageStore.order_end_open_date_filter) {
            startStr = pageStore.order_start_open_date_filter;
            endStr = pageStore.order_end_open_date_filter;
          }
          break;
        default:
          break;
      }

      if (startStr && endStr) {
        sortableData = sortableData.filter((item) => {
          // Только если есть дата, применяем фильтр
          if (!item.AccOpenDate) return false;
          return item.AccOpenDate >= startStr && item.AccOpenDate <= endStr;
        });
      }
    }

    // Фильтр по заполненности даты закрытия
    if (pageStore.order_type_date_end_filter === "fill") {
      sortableData = sortableData.filter(item => item.ClosDate != null && item.ClosDate !== '');
    } else if (pageStore.order_type_date_end_filter === "empty") {
      sortableData = sortableData.filter(item => !item.ClosDate || item.ClosDate === '');
    } else if (pageStore.order_type_date_end_filter !== "none") {
      // Фильтр по дате закрытия счета (только если не выбраны fill/empty/none)
      let startStr = null;
      let endStr = null;
      const currentDate = new Date();

      switch (pageStore.order_type_date_end_filter) {
        case "week":
          const monday = getMonday(currentDate);
          startStr = pageStore.toISODateString(monday);
          endStr = pageStore.toISODateString(
            new Date(monday.getTime() + 6 * 24 * 60 * 60 * 1000)
          );
          break;
        case "month":
          startStr = pageStore.toISODateString(
            new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
          );
          endStr = pageStore.toISODateString(
            new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
          );
          break;
        case "pastMonth":
          startStr = pageStore.toISODateString(
            new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
          );
          endStr = pageStore.toISODateString(
            new Date(currentDate.getFullYear(), currentDate.getMonth(), 0)
          );
          break;
        case "quarter":
          const quarter = Math.floor(currentDate.getMonth() / 3);
          startStr = pageStore.toISODateString(
            new Date(currentDate.getFullYear(), quarter * 3, 1)
          );
          endStr = pageStore.toISODateString(
            new Date(currentDate.getFullYear(), quarter * 3 + 3, 0)
          );
          break;
        case "halfYear":
          const half = Math.floor(currentDate.getMonth() / 6);
          startStr = pageStore.toISODateString(
            new Date(currentDate.getFullYear(), half * 6, 1)
          );
          endStr = pageStore.toISODateString(
            new Date(currentDate.getFullYear(), half * 6 + 6, 0)
          );
          break;
        case "year":
          startStr = pageStore.toISODateString(new Date(currentDate.getFullYear(), 0, 1));
          endStr = pageStore.toISODateString(
            new Date(currentDate.getFullYear(), 11, 31)
          );
          break;
        case "pastYear":
          startStr = pageStore.toISODateString(new Date(currentDate.getFullYear() - 1, 0, 1));
          endStr = pageStore.toISODateString(
            new Date(currentDate.getFullYear() - 1, 11, 31)
          )
          break;
        case "custom":
          if (pageStore.order_start_close_date_filter && pageStore.order_end_close_date_filter) {
            startStr = pageStore.order_start_close_date_filter;
            endStr = pageStore.order_end_close_date_filter;
            // Убедимся, что даты установлены в store
            pageStore.setOrderStartCloseDateFilter(startStr);
            pageStore.setOrderEndCloseDateFilter(endStr);
          }
          break;
        default:
          break;
      }

      if (startStr && endStr) {
        sortableData = sortableData.filter((item) => {
          if (!item.ClosDate) return false;
          return item.ClosDate >= startStr && item.ClosDate <= endStr;
        });
      }
    }

    // Фильтры прогноза ДЗ / готовности / у клиента и фактической даты отгрузки.
    // Попутно запоминаем, какой из них обнулил непустую выборку: только в этом
    // случае подсказка под таблицей утверждает что-то доказуемое.
    const dateFilterSteps = [
      ["Дата получения ДЗ (прогноз)", pageStore.order_type_dz_date_filter, "DzReceiveDate",
        pageStore.order_start_dz_date_filter, pageStore.order_end_dz_date_filter],
      ["Дата готовности груза (прогноз)", pageStore.order_type_cargo_ready_filter, "CargoReadyDate",
        pageStore.order_start_cargo_ready_filter, pageStore.order_end_cargo_ready_filter],
      ["Дата у клиента (прогноз)", pageStore.order_type_date_at_client_filter, "DateAtClient",
        pageStore.order_start_date_at_client_filter, pageStore.order_end_date_at_client_filter],
      ["Дата отгрузки (факт)", pageStore.order_type_shipment_date_filter, "ShipmentDate",
        pageStore.order_start_shipment_date_filter, pageStore.order_end_shipment_date_filter],
    ];

    for (const [label, filterType, field, customStart, customEnd] of dateFilterSteps) {
      const countBefore = sortableData.length;
      sortableData = filterByForecastDate(sortableData, filterType, field, customStart, customEnd);
      // Подсказка только про фильтр по периоду: у «Есть/Нет даты» пустой
      // результат означает другое, и такой текст был бы неверным.
      const isPeriodFilter =
        filterType && !["none", "fill", "empty"].includes(filterType);
      if (isPeriodFilter && countBefore > 0 && sortableData.length === 0 && !emptyReason) {
        emptyReason = label;
      }
    }

    // Фильтр % отгрузки (мультивыбор чекбоксами)
    sortableData = sortableData.filter((item) =>
      matchesShipmentPercent(item.PercentShipment, pageStore.order_shipment_percent_filters)
    );

    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        const valueA = a[sortConfig.key];
        const valueB = b[sortConfig.key];

        if (typeof valueA === "number" && typeof valueB === "number") {
          return sortConfig.direction === "asc"
            ? valueA - valueB
            : valueB - valueA;
        }

        if (
          sortConfig.key === "Date" ||
          sortConfig.key === "AccOpenDate" ||
          sortConfig.key === "ClosDate" ||
          sortConfig.key === "ShipmentDate" ||
          sortConfig.key === "CargoReadyDate" ||
          sortConfig.key === "DateInSpb" ||
          sortConfig.key === "DateAtClient" ||
          sortConfig.key === "DzReceiveDate"
        ) {
          const dateA = valueA ? new Date(valueA) : null;
          const dateB = valueB ? new Date(valueB) : null;

          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;

          return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
        }

        const strA = String(valueA || "");
        const strB = String(valueB || "");

        return sortConfig.direction === "asc"
          ? strA.localeCompare(strB)
          : strB.localeCompare(strA);
      });
    }

    return { rows: sortableData, emptyReason };
  }, [
    data,
    sortConfig,
    pageStore.order_num_filter,
    pageStore.order_type_date_start_filter,
    pageStore.order_type_date_end_filter,
    pageStore.order_type_dz_date_filter,
    pageStore.order_type_cargo_ready_filter,
    pageStore.order_type_date_at_client_filter,
    pageStore.order_type_shipment_date_filter,
    pageStore.order_start_open_date_filter,
    pageStore.order_end_open_date_filter,
    pageStore.order_start_close_date_filter,
    pageStore.order_end_close_date_filter,
    pageStore.order_start_dz_date_filter,
    pageStore.order_end_dz_date_filter,
    pageStore.order_start_cargo_ready_filter,
    pageStore.order_end_cargo_ready_filter,
    pageStore.order_start_date_at_client_filter,
    pageStore.order_end_date_at_client_filter,
    pageStore.order_start_shipment_date_filter,
    pageStore.order_end_shipment_date_filter,
    pageStore.order_shipment_percent_filters,
    pageStore.isSelf,
    pageStore.selected_view_orders,
    pageStore.workers,
    pageStore.order_deal_filter
  ]);

  // Строки после фильтров страницы — база для списков значений «⏷».
  const preColumnData = sortedResult.rows;
  const emptyByDateFilter = sortedResult.emptyReason;


  const availableColumns = pageStore.columnConfigDebitor.filter(
    (col) => data.length > 0 && (data[0].hasOwnProperty(col.key) || col.key === "Total") && col.active
  );

  // Аналитический фильтр «⏷» по значениям, как в «Просроченной ДЗ»: у всех
  // столбцов счёта, кроме расчётного «Total». Отбор — до группировки, итоги
  // считаются по прошедшим строкам.
  const filterableKeys = availableColumns
    .map((col) => col.key)
    .filter((key) => data.length > 0 && data[0].hasOwnProperty(key));
  const [columnFilters, setColumnFilters] = useColumnFilters(data, filterableKeys);
  const sortedData = useMemo(
    () => rowsPassing(preColumnData, columnFilters),
    [preColumnData, columnFilters]
  );
  const filterableSet = new Set(filterableKeys);

  const handleExportToXLSX = async () => {
    var can = await pageStore.CheckPermission("export excel")
    if (!can) {
      toast({
        title: "У вас нет права экспорта в excel",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return
    }
    const exportRows = [];
    const columns = availableColumns;

    if (pageStore.selected_view_orders !== "all") {
      const grouped = groupData(sortedData, groupBy);

      exportRows.push(["Отчет по сделкам"]);

      grouped.forEach((group) => {
        // Строка группы
        exportRows.push([`Группа: ${group.key}`]);

        // Заголовок колонок
        exportRows.push(columns.map(col => col.title));

        group.items.forEach((item) => {
          const row = columns.map(col => formatCellValueExcel(item[col.key]));
          exportRows.push(row);
        });

        // Подитог по группе
        const totalSum = group.items.reduce((acc, curr) => acc + (curr.RevRub || 0), 0);
        const paidRubSum = group.items.reduce((acc, curr) => acc + (curr.PaidRub || 0), 0);
        const deptRubSum = group.items.reduce((acc, curr) => acc + (curr.DeptRub || 0), 0);
        const percentPayment = totalSum ? (paidRubSum / totalSum) * 100 : 0;
        const percentShipment = totalSum ? (deptRubSum / totalSum) * 100 : 0;

        const totalRow = columns.map((col, idx) => {
          if (idx === 0) return `Итого по группе: ${group.key}`;
          if (col.key === "Num") return group.items.length;
          if (col.key === "RevRub") return totalSum.toLocaleString("ru-RU");
          if (col.key === "PaidRub") return paidRubSum.toLocaleString("ru-RU");
          if (col.key === "DeptRub") return deptRubSum.toLocaleString("ru-RU");
          if (col.key === "PercentPayment") return `${percentPayment.toFixed(2)}%`;
          if (col.key === "PercentShipment") return `${percentShipment.toFixed(2)}%`;
          if (col.key === "Total") return totalSum.toLocaleString("ru-RU");
          return "";
        });
        exportRows.push(totalRow);

        // Пустая строка между группами
        exportRows.push([]);
      });

      // Общий итог
      const grandTotal = sortedData.reduce((acc, curr) => acc + (curr.RevRub || 0), 0);
      const grandPaidRub = sortedData.reduce((acc, curr) => acc + (curr.PaidRub || 0), 0);
      const grandDeptRub = sortedData.reduce((acc, curr) => acc + (curr.DeptRub || 0), 0);
      const grandPercentPayment = grandTotal ? (grandPaidRub / grandTotal) * 100 : 0;
      const grandPercentShipment = grandTotal ? (grandDeptRub / grandTotal) * 100 : 0;

      const grandTotalRow = columns.map((col, idx) => {
        if (idx === 0) return "Общий итог";
        if (col.key === "Num") return sortedData.length;
        if (col.key === "RevRub") return grandTotal;
        if (col.key === "PaidRub") return grandPaidRub;
        if (col.key === "DeptRub") return grandDeptRub;
        if (col.key === "PercentPayment") return Number(grandPercentPayment.toFixed(2));
        if (col.key === "PercentShipment") return Number(grandPercentShipment.toFixed(2));
        if (col.key === "Total") return grandTotal;
        return "";
      });
      exportRows.push(grandTotalRow);
    } else {
      // Без группировки
      exportRows.push(["Отчет по сделкам"]);
      exportRows.push(columns.map(col => col.title));
      sortedData.forEach((item) => {
        const row = columns.map(col => formatCellValueExcel(item[col.key]));
        exportRows.push(row);
      });

      // Общий итог
      const grandTotal = sortedData.reduce((acc, curr) => acc + (curr.RevRub || 0), 0);
      const grandPaidRub = sortedData.reduce((acc, curr) => acc + (curr.PaidRub || 0), 0);
      const grandDeptRub = sortedData.reduce((acc, curr) => acc + (curr.DeptRub || 0), 0);
      const grandPercentPayment = grandTotal ? (grandPaidRub / grandTotal) * 100 : 0;
      const grandPercentShipment = grandTotal ? (grandDeptRub / grandTotal) * 100 : 0;

      const grandTotalRow = columns.map((col, idx) => {
        if (idx === 0) return "Общий итог";
        if (col.key === "RevRub") return grandTotal;
        if (col.key === "PaidRub") return grandPaidRub;
        if (col.key === "DeptRub") return grandDeptRub;
        if (col.key === "PercentPayment") return Number(grandPercentPayment.toFixed(2));
        if (col.key === "PercentShipment") return Number(grandPercentShipment.toFixed(2));
        if (col.key === "Total") return grandTotal;
        return "";
      });
      exportRows.push(grandTotalRow);
    }

    // Экспорт в Excel
    const worksheet = XLSX.utils.aoa_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Deals Export");
    XLSX.writeFile(workbook, "deals-export.xlsx");
  };

  const formatCellValue = (value) => {
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-");
      return `${day}.${month}.${year}`;
    }
    if (typeof value === "number") {
      return value.toLocaleString("ru-RU");
    }
    return value || "";
  };

  const formatCellValueExcel = excelCellValue;

  const [groupBy, setGroupBy] = useState("manager");
  const [expandedGroups, setExpandedGroups] = useState({});

  const groupData = (data, groupBy, sortConfig = null) => {
    const grouped = data.reduce((acc, item) => {
      let key;

      if (pageStore.selected_view_orders === "manager") {
        key = item.Manager;
      } else if (pageStore.selected_view_orders === "org") {
        key = item.Org;
      } else if (pageStore.selected_view_orders === "departament") {
        key = item.departament_name
      } else if (pageStore.selected_view_orders === "departament-withoutROP") {
        key = item.departament_name
      } else if (pageStore.selected_view_orders === "partner") {
        key = item.Partner
      } else if (groupBy === "month") {
        const date = new Date(item.DateDoc);
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
          2,
          "0"
        )}`;
      } else if (groupBy === "year") {
        const date = new Date(item.DateDoc);
        key = date.getFullYear();
      }
      if (!key) return acc;

      if (!acc[key]) {
        acc[key] = {
          items: [],
          totalSum: 0,
          paidRubSum: 0,
          deptRubSum: 0,
        };
      }

      acc[key].items.push(item);
      acc[key].totalSum += item.RevRub || 0;
      acc[key].paidRubSum += item.PaidRub || 0;
      acc[key].deptRubSum += item.DeptRub || 0;

      return acc;
    }, {});

    // Преобразуем объект в массив и сортируем группы
    let groupsArray = Object.entries(grouped).map(([key, value]) => ({
      key,
      items: value.items,
      totalSum: value.totalSum,
      paidRubSum: value.paidRubSum,
      deptRubSum: value.deptRubSum,
      percentPayment: value.totalSum ? (value.paidRubSum / value.totalSum) * 100 : 0,
      percentShipment: value.totalSum ? (value.deptRubSum / value.totalSum) * 100 : 0,
    }));

    // Сортируем группы, если указана сортировка
    if (sortConfig && sortConfig.key) {
      groupsArray.sort((a, b) => {
        let valueA, valueB;

        // Для групповых заголовков используем агрегированные значения
        if (sortConfig.key === "RevRub") {
          valueA = a.totalSum;
          valueB = b.totalSum;
        } else if (sortConfig.key === "PaidRub") {
          valueA = a.paidRubSum;
          valueB = b.paidRubSum;
        } else if (sortConfig.key === "DeptRub") {
          valueA = a.deptRubSum;
          valueB = b.deptRubSum;
        } else if (sortConfig.key === "PercentPayment") {
          valueA = a.percentPayment;
          valueB = b.percentPayment;
        } else if (sortConfig.key === "PercentShipment") {
          valueA = a.percentShipment;
          valueB = b.percentShipment;
        } else {
          // Для остальных столбцов используем ключ группы
          valueA = a.key;
          valueB = b.key;
        }

        // Применяем сортировку
        if (typeof valueA === "number" && typeof valueB === "number") {
          return sortConfig.direction === "asc" ? valueA - valueB : valueB - valueA;
        } else {
          // Для строк
          return sortConfig.direction === "asc"
            ? String(valueA || "").localeCompare(String(valueB || ""))
            : String(valueB || "").localeCompare(String(valueA || ""));
        }
      });
    }

    return groupsArray;
  };

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const groupedData = groupBy ? groupData(sortedData, groupBy, sortConfig) : null;

  const user = pageStore.all_managers?.find(
    (item) => item?.email == pageStore.email
  );

  // Рассчитываем общие итоги
  const grandTotals = useMemo(() => {
    return {
      RevRub: sortedData.reduce((acc, curr) => acc + (curr.RevRub || 0), 0),
      PaidRub: sortedData.reduce((acc, curr) => acc + (curr.PaidRub || 0), 0),
      DeptRub: sortedData.reduce((acc, curr) => acc + (curr.DeptRub || 0), 0),
      Count: sortedData.length,
    };
  }, [sortedData]);

  const grandPercentPayment = grandTotals.RevRub ? (grandTotals.PaidRub / grandTotals.RevRub) * 100 : 0;
  const grandPercentShipment = grandTotals.RevRub ? (grandTotals.DeptRub / grandTotals.RevRub) * 100 : 0;

  // Формируем плоский список данных для виртуализированного списка
  const flatData = useMemo(() => {
    const result = [];

    // Всегда добавляем строку "Итого" в начало
    result.push({
      type: 'grand-total',
      totals: grandTotals,
      percentPayment: grandPercentPayment,
      percentShipment: grandPercentShipment,
    });

    if (pageStore.selected_view_orders !== "all" && groupedData) {
      groupedData.forEach(group => {
        // Добавляем строку заголовка группы
        result.push({
          type: 'group-header',
          groupKey: group.key,
          group,
        });

        // Если группа развернута, добавляем ее элементы
        if (expandedGroups[group.key]) {
          group.items.forEach((item, index) => {
            result.push({
              type: 'row',
              item,
              groupKey: group.key,
              isFirst: index === 0,
              isLast: index === group.items.length - 1,
              groupTotal: group.totalSum,
              groupPaidRub: group.paidRubSum,
              groupDeptRub: group.deptRubSum,
              groupPercentPayment: group.percentPayment,
              groupPercentShipment: group.percentShipment,
            });
          });
        }
      });
    } else {
      // Для режима без группировки
      sortedData.forEach(item => {
        result.push({
          type: 'row',
          item,
          isFirst: false,
          isLast: false,
        });
      });
    }

    return result;
  }, [groupedData, expandedGroups, sortedData, pageStore.selected_view_orders, grandTotals, grandPercentPayment, grandPercentShipment, availableColumns]);

  const buttons = (
    <>
      <Button
        onClick={handleExportToXLSX}
        bg="#333"
        color="white"
        width={"15vw"}
        _hover={{
          background: "#444",
        }}
        mr={3}
      >
        Экспорт в Excel
      </Button>
      <Button
        onClick={() => {
          // Развернуть все группы
          if (groupedData) {
            const allExpanded = {};
            groupedData.forEach((group) => {
              allExpanded[group.key] = true;
            });
            setExpandedGroups(allExpanded);
          }
        }}
        bg="#333"
        color="white"
        width={"15vw"}
        _hover={{
          background: "#444",
        }}
        mr={3}
      >
        Развернуть всё
      </Button>
      <Button
        onClick={() => {
          // Свернуть все группы
          setExpandedGroups({});
        }}
        bg="#333"
        color="white"
        width={"15vw"}
        _hover={{
          background: "#444",
        }}
      >
        Свернуть всё
      </Button>
    </>
  )

  return (
    <>
      <Collapse in={pageStore.open_filter} animateOpacity>
        {renderButtons ? renderButtons(buttons) : buttons}
      </Collapse>

      {pageStore?.data_debitors.length ?

        <VStack alignItems={"flex-start"} spacing={4} w="100%">
          <DateFilters />
          <ResetColumnFiltersButton filters={columnFilters} onReset={() => setColumnFilters({})} />
          {emptyByDateFilter && (
            <Box
              w="100%"
              bg="#2B2517"
              borderWidth="1px"
              borderColor="#7A6A2F"
              borderRadius="md"
              p={3}
            >
              <Text color="#F0D98C" fontSize="15px">
                Фильтр «{emptyByDateFilter}» отобрал 0 строк: среди загруженных счетов
                нет ни одного с этой датой в выбранном периоде. Загружаются только счета
                за «Период счетов» вверху страницы — расширьте его и нажмите «Сформировать».
              </Text>
            </Box>
          )}
          <Box minH={"100vh"} overflow={"scroll"} borderRadius="md" borderWidth="1px" borderColor="gray.300">
            <Box
              display="flex"
              bg="black"
              color="white"
              p={"10px"}
              fontWeight="normal"
              position="sticky"
              top={0}
            >
              {pageStore.selected_view_orders !== "all" && (
                <Box
                  key="group-header"
                  width="250px"
                  minWidth="250px"
                  maxWidth="250px"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  px={2}
                  py={pageStore.rowHeight}
                >
                  <Text>Группа</Text>
                </Box>
              )}

              {availableColumns.map((column) => (
                <Box
                  key={column.key}
                  width={column.width}
                  minWidth={column.width}
                  maxWidth={column.width}
                  overflow="hidden"
                  textOverflow="ellipsis"
                  cursor="pointer"
                  onClick={() => handleSort(column.key)}
                  px={2}
                  py={pageStore.rowHeight}
                >
                  <Text fontSize="xl">
                    <HeaderTitle
                      title={column.title}
                      suffix={sortConfig.key === column.key ? (sortConfig.direction === "asc" ? " ↑" : " ↓") : null}
                      filter={
                        filterableSet.has(column.key) && (
                          <ColumnValueFilter
                            columnKey={column.key}
                            title={column.title}
                            rows={preColumnData}
                            filters={columnFilters}
                            onChange={setColumnFilters}
                          />
                        )
                      }
                    />
                  </Text>
                </Box>
              ))}
            </Box>

            <Box height="100vh">
              <AutoSizer>
                {({ height, width }) => (
                  <List
                    height={height}
                    itemCount={flatData.length}
                    itemSize={160}
                    width={width}
                  >
                    {({ index, style }) => {
                      const item = flatData[index];

                      // Рендеринг строки "Итого"
                      if (item.type === 'grand-total') {
                        return (
                          <Box
                            display="flex"
                            style={style}
                            bg="#2a4365"
                            px={"10px"}
                            py={2}
                            color="white"
                            fontWeight="bold"
                            alignItems="center"
                          >
                            {/* Режим с группировкой */}
                            {pageStore.selected_view_orders !== "all" && (
                              <>
                                <Box
                                  width="250px"
                                  minWidth="250px"
                                  maxWidth="250px"
                                  overflow="hidden"
                                  textOverflow="ellipsis"
                                  textAlign={"left"}
                                  px={2}
                                  py={pageStore.rowHeight}
                                >
                                  Итого {sortedData.length}
                                </Box>

                                {availableColumns.map((column, idx) => {
                                  if (column.key === "Num") {
                                    return (
                                      <Box
                                        key={column.key}
                                        width={column.width}
                                        minWidth={column.width}
                                        maxWidth={column.width}
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        textAlign={"left"}
                                        fontWeight="bold"
                                        px={2}
                                        py={pageStore.rowHeight}
                                      >
                                        {item.totals.Count} {/* Отображаем количество */}
                                      </Box>
                                    );
                                  }
                                  if (column.key === "RevRub") {
                                    return (
                                      <Box
                                        key={column.key}
                                        width={column.width}
                                        minWidth={column.width}
                                        maxWidth={column.width}
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        textAlign={"left"}
                                        fontWeight="bold"
                                        px={2}
                                        py={pageStore.rowHeight}
                                      >
                                        {formatCellValue(item.totals.RevRub)}
                                      </Box>
                                    );
                                  } else if (column.key === "PaidRub") {
                                    return (
                                      <Box
                                        key={column.key}
                                        width={column.width}
                                        minWidth={column.width}
                                        maxWidth={column.width}
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        textAlign={"left"}
                                        fontWeight="bold"
                                        px={2}
                                        py={pageStore.rowHeight}
                                      >
                                        {formatCellValue(item.totals.PaidRub)}
                                      </Box>
                                    );
                                  } else if (column.key === "DeptRub") {
                                    return (
                                      <Box
                                        key={column.key}
                                        width={column.width}
                                        minWidth={column.width}
                                        maxWidth={column.width}
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        textAlign={"left"}
                                        fontWeight="bold"
                                        px={2}
                                        py={pageStore.rowHeight}
                                      >
                                        {formatCellValue(item.totals.DeptRub)}
                                      </Box>
                                    );
                                  } else if (column.key === "PercentPayment") {
                                    return (
                                      <Box
                                        key={column.key}
                                        width={column.width}
                                        minWidth={column.width}
                                        maxWidth={column.width}
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        textAlign={"left"}
                                        fontWeight="bold"
                                        px={2}
                                        py={pageStore.rowHeight}
                                      >
                                        {`${item.percentPayment.toFixed(2)}%`}
                                      </Box>
                                    );
                                  } else if (column.key === "PercentShipment") {
                                    return (
                                      <Box
                                        key={column.key}
                                        width={column.width}
                                        minWidth={column.width}
                                        maxWidth={column.width}
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        textAlign={"left"}
                                        fontWeight="bold"
                                        px={2}
                                        py={pageStore.rowHeight}
                                      >
                                        {`${item.percentShipment.toFixed(2)}%`}
                                      </Box>
                                    );
                                  } else if (column.key === "Total") {
                                    return (
                                      <Box
                                        key={column.key}
                                        width={column.width}
                                        minWidth={column.width}
                                        maxWidth={column.width}
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        textAlign={"left"}
                                        fontWeight="bold"
                                        px={2}
                                        py={pageStore.rowHeight}
                                      >
                                        {formatCellValue(item.totals.RevRub)}
                                      </Box>
                                    );
                                  } else {
                                    return (
                                      <Box
                                        key={column.key}
                                        width={column.width}
                                        minWidth={column.width}
                                        maxWidth={column.width}
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        textAlign={"left"}
                                        px={2}
                                        py={pageStore.rowHeight}
                                      />
                                    );
                                  }
                                })}
                              </>
                            )}

                            {/* Режим без группировки */}
                            {pageStore.selected_view_orders === "all" && (
                              <>
                                {availableColumns.map((column, idx) => {
                                  if (idx === 0) {
                                    return (
                                      <Box
                                        key={column.key}
                                        width={column.width}
                                        minWidth={column.width}
                                        maxWidth={column.width}
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        textAlign={"left"}
                                        px={2}
                                        py={pageStore.rowHeight}
                                      >
                                        Итого {sortedData.length}
                                      </Box>
                                    );
                                  }
                                  else if (column.key === "Num") {
                                    return (
                                      <Box
                                        key={column.key}
                                        width={column.width}
                                        minWidth={column.width}
                                        maxWidth={column.width}
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        textAlign="left"
                                        fontWeight="bold"
                                        px={2}
                                        py={pageStore.rowHeight}
                                      >
                                        {item.totals.Count} {/* Отображаем количество */}
                                      </Box>
                                    );
                                  }
                                  else if (column.key === "RevRub") {
                                    return (
                                      <Box
                                        key={column.key}
                                        width={column.width}
                                        minWidth={column.width}
                                        maxWidth={column.width}
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        textAlign="left"
                                        fontWeight="bold"
                                        px={2}
                                        py={pageStore.rowHeight}
                                      >
                                        {formatCellValue(item.totals.RevRub)}
                                      </Box>
                                    );
                                  } else if (column.key === "PaidRub") {
                                    return (
                                      <Box
                                        key={column.key}
                                        width={column.width}
                                        minWidth={column.width}
                                        maxWidth={column.width}
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        textAlign="left"
                                        fontWeight="bold"
                                        px={2}
                                        py={pageStore.rowHeight}
                                      >
                                        {formatCellValue(item.totals.PaidRub)}
                                      </Box>
                                    );
                                  } else if (column.key === "DeptRub") {
                                    return (
                                      <Box
                                        key={column.key}
                                        width={column.width}
                                        minWidth={column.width}
                                        maxWidth={column.width}
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        textAlign="left"
                                        fontWeight="bold"
                                        px={2}
                                        py={pageStore.rowHeight}
                                      >
                                        {formatCellValue(item.totals.DeptRub)}
                                      </Box>
                                    );
                                  } else if (column.key === "PercentPayment") {
                                    return (
                                      <Box
                                        key={column.key}
                                        width={column.width}
                                        minWidth={column.width}
                                        maxWidth={column.width}
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        textAlign="left"
                                        fontWeight="bold"
                                        px={2}
                                        py={pageStore.rowHeight}
                                      >
                                        {`${item.percentPayment.toFixed(2)}%`}
                                      </Box>
                                    );
                                  } else if (column.key === "PercentShipment") {
                                    return (
                                      <Box
                                        key={column.key}
                                        width={column.width}
                                        minWidth={column.width}
                                        maxWidth={column.width}
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        textAlign="left"
                                        fontWeight="bold"
                                        px={2}
                                        py={pageStore.rowHeight}
                                      >
                                        {`${item.percentShipment.toFixed(2)}%`}
                                      </Box>
                                    );
                                  } else if (column.key === "Total") {
                                    return (
                                      <Box
                                        key={column.key}
                                        width={column.width}
                                        minWidth={column.width}
                                        maxWidth={column.width}
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        textAlign="left"
                                        fontWeight="bold"
                                        px={2}
                                        py={pageStore.rowHeight}
                                      >
                                        {formatCellValue(item.totals.RevRub)}
                                      </Box>
                                    );
                                  } else {
                                    return (
                                      <Box
                                        key={column.key}
                                        width={column.width}
                                        minWidth={column.width}
                                        maxWidth={column.width}
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        textAlign={"left"}
                                        px={2}
                                        py={pageStore.rowHeight}
                                      />
                                    );
                                  }
                                })}
                              </>
                            )}
                          </Box>
                        );
                      }

                      // Рендеринг строки-заголовка группы
                      if (item.type === 'group-header') {
                        return (
                          <Box
                            display="flex"
                            style={style}
                            bg="#444"
                            px={"10px"}
                            py={2}
                            color="white"
                            alignItems="center"
                          >
                            <Box
                              width="250px"
                              minWidth="250px"
                              maxWidth="250px"
                              overflow="hidden"
                              textOverflow="ellipsis"
                              textAlign={"left"}
                              px={2}
                              display="flex"
                              py={pageStore.rowHeight}
                              alignItems="center"
                            >
                              <Button
                                color="white"
                                fontSize="25px"
                                _hover={{ bg: "black", color: "white" }}
                                size="sm"
                                bgColor="#444"
                                onClick={() => toggleGroup(item.groupKey)}
                                mr={2}
                              >
                                {expandedGroups[item.groupKey] ? "-" : "+"}
                              </Button>
                              <Text
                                cursor="pointer"
                                onClick={() => toggleGroup(item.groupKey)}
                                textAlign="left"
                                fontSize={"20px"}
                                fontWeight="normal"
                              >
                                {item.groupKey}
                              </Text>
                            </Box>

                            {availableColumns.map((column) => {
                              if (column.key === "RevRub") {
                                return (
                                  <Box
                                    key={column.key}
                                    width={column.width}
                                    minWidth={column.width}
                                    maxWidth={column.width}
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                    textAlign="left"
                                    fontWeight="bold"
                                    px={2}
                                    py={pageStore.rowHeight}
                                  >
                                    {formatCellValue(item.group.totalSum)}
                                  </Box>
                                );
                              } else if (column.key === "PaidRub") {
                                return (
                                  <Box
                                    key={column.key}
                                    width={column.width}
                                    minWidth={column.width}
                                    maxWidth={column.width}
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                    textAlign="left"
                                    fontWeight="bold"
                                    px={2}
                                    py={pageStore.rowHeight}
                                  >
                                    {formatCellValue(item.group.paidRubSum)}
                                  </Box>
                                );
                              } else if (column.key === "DeptRub") {
                                return (
                                  <Box
                                    key={column.key}
                                    width={column.width}
                                    minWidth={column.width}
                                    maxWidth={column.width}
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                    textAlign="left"
                                    fontWeight="bold"
                                    px={2}
                                    py={pageStore.rowHeight}
                                  >
                                    {formatCellValue(item.group.deptRubSum)}
                                  </Box>
                                );
                              } else if (column.key === "PercentPayment") {
                                return (
                                  <Box
                                    key={column.key}
                                    width={column.width}
                                    minWidth={column.width}
                                    maxWidth={column.width}
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                    textAlign="left"
                                    fontWeight="bold"
                                    px={2}
                                    py={pageStore.rowHeight}
                                  >
                                    {`${item.group.percentPayment.toFixed(2)}%`}
                                  </Box>
                                );
                              } else if (column.key === "PercentShipment") {
                                return (
                                  <Box
                                    key={column.key}
                                    width={column.width}
                                    minWidth={column.width}
                                    maxWidth={column.width}
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                    textAlign="left"
                                    fontWeight="bold"
                                    px={2}
                                    py={pageStore.rowHeight}
                                  >
                                    {`${item.group.percentShipment.toFixed(2)}%`}
                                  </Box>
                                );
                              } else if (column.key === "Total") {
                                return (
                                  <Box
                                    key={column.key}
                                    width={column.width}
                                    minWidth={column.width}
                                    maxWidth={column.width}
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                    textAlign="left"
                                    fontWeight="bold"
                                    px={2}
                                    py={pageStore.rowHeight}
                                  >
                                    {formatCellValue(item.group.totalSum)}
                                  </Box>
                                );
                              } else {
                                return (
                                  <Box
                                    key={column.key}
                                    width={column.width}
                                    minWidth={column.width}
                                    maxWidth={column.width}
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                    textAlign={"left"}
                                    px={2}
                                  />
                                );
                              }
                            })}
                          </Box>
                        );
                      }

                      // Рендеринг обычной строки данных
                      return (
                        <Box
                          display="flex"
                          style={style}
                          bg={index % 2 === 0 ? "#555" : "#666"}
                          px={"10px"}
                          py={2}
                          color="white"
                        >
                          {/* Столбец группы для первой строки в группе */}
                          {pageStore.selected_view_orders !== "all" && (
                            <Box
                              width="250px"
                              minWidth="250px"
                              maxWidth="250px"
                              overflow="hidden"
                              textOverflow="ellipsis"
                              textAlign={"left"}
                              px={2}
                              py={pageStore.rowHeight}
                            >
                              {item.isFirst ? item.groupKey : ""}
                            </Box>
                          )}

                          {availableColumns.map((column) => {
                            if (column.key === "Total") {
                              return (
                                <Box
                                  key={column.key}
                                  width={column.width}
                                  minWidth={column.width}
                                  maxWidth={column.width}
                                  overflow="hidden"
                                  textOverflow="ellipsis"
                                  textAlign="left"
                                  fontWeight={item.isLast ? "bold" : "normal"}
                                  px={2}
                                  py={pageStore.rowHeight}
                                >
                                  {pageStore.selected_view_orders !== "all" && item.isLast
                                    ? formatCellValue(item.groupTotal)
                                    : ""}
                                </Box>
                              );
                            } else {
                              return (
                                <Box
                                  key={column.key}
                                  width={column.width}
                                  minWidth={column.width}
                                  maxWidth={column.width}
                                  overflow="hidden"
                                  textOverflow="ellipsis"
                                  textAlign={"left"}
                                  px={2}
                                  fontStyle={item.item.active ? "normal" : "italic"}
                                  py={pageStore.rowHeight}
                                >
                                  {renderCellContent(column.key, item.item[column.key])}
                                </Box>
                              );
                            }
                          })}
                        </Box>
                      );
                    }}
                  </List>
                )}
              </AutoSizer>
            </Box>
          </Box>
        </VStack>
        : null}
    </>
  );
});

export default DataTable;